import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Box, Paper, Table, TableBody, TableContainer, TableHead, TableRow, Tooltip, Collapse, Switch } from '@mui/material';
import CommonFilterToggle from '../../../../form-components/CommonFilterToggle';
import { useToast } from 'use-toast-mui';
import qs from 'qs';

import AnimationPage from '../../../../pages/AnimationPage';
import { getAllTickets } from '../../../../api/ticket';
import { saleBranchLabel, usageBranchLabel } from '../../../../utils/branchShare';
import { toggleTicketExposure } from '../../../../api/exposure';
import { FormInputText } from '../../../../form-components/FormInputText';
import { classTypes, order, pageSize, salesStatus, sort1 } from '../../../../constants/constants';
import { FormInputDropdown } from '../../../../form-components/FormInputDropdown';
import { RHFCheckbox } from '../../../CheckBox';
import { errorMessages, formatterCurrency } from '../../../../utils/methods';

import {
  PageContainer,
  SearchSection,
  SearchInputWrapper,
  SearchLabel,
  SearchButton,
  CreateButton,
  FilterRow,
  FilterLabel,
  FilterButtonWrapper,
  ApplyButton,
  ClearButton,
  Divider,
  TableControlsWrapper,
  PageInfo,
  SortWrapper,
  StyledTableCell,
  StyledTableCellLast,
  StyledBodyCell,
  StyledBodyCellLast,
  StyledTableRow,
  StatusText,
  SalesOptionSection,
  SalesOptionLabel,
  SalesOptionContent,
  SalesOptionRow,
  SubLabel,
  DirectInputWrapper,
  DirectInputLabel,
  DirectInputField,
  DirectInputUnit,
  StyledCheckbox,
  CheckboxLabel,
  AllCheckboxLabel,
  FilterDivider,
  AllCheckboxWrapper,
  FilterContent,
} from './TicketListStyled';
import { centersDetail } from '../../../../api/store-management';
import PlusIcon from '../../../../_icons/PlusIcon';
import CommonSearchSubmitButton from '../../../../form-components/CommonSearchSubmitButton';
import CommonFilterActionButton from '../../../../form-components/CommonFilterActionButton';
import CommonPagination from '../../../../form-components/CommonPagination';
import useUserAuth from '../../../../utils/useUserAuth';
import ConfirmModal from '../../../common/modal/ConfirmModal';
import FavoriteStar from '../../../common/FavoriteStar';
import { FAVORITE_TARGET } from '../../../../api/favorite';
import TicketGuideModal from './TicketGuideModal';
import { GuideButton } from '../../../lesson/groupLesson/GroupLessonStyled';

// 수강권은 듀엣레슨 상품도 있어 공용 classTypes 에 듀엣을 끼운다 (공용 상수는 다른 화면도 써서 그대로 둔다)
const TICKET_CLASS_TYPES = [
  classTypes[0],
  { label: 'DUET_LESSON', value: 'DUET_LESSON' },
  ...classTypes.slice(1),
];

const COUNT_OPTIONS = [
  { label: '5회', value: 5 },
  { label: '10회', value: 10 },
  { label: '15회', value: 15 },
  { label: '20회', value: 20 },
];

const PERIOD_OPTIONS = [
  { label: '1개월', value: 1 },
  { label: '3개월', value: 3 },
  { label: '6개월', value: 6 },
  { label: '12개월', value: 12 },
];

// 다지점 공유 상품(#98) 지점 컬럼 — 판매/이용 요약 두 줄
const BranchShareText = ({ children }) => (
  <span style={{ display: 'inline-flex', flexDirection: 'column', fontSize: '12px', lineHeight: 1.4, whiteSpace: 'nowrap' }}>
    {children}
  </span>
);

const TicketList = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const user = useSelector(state => state.user.user);
  const canCreate = useUserAuth('TICKET_COURSE_CREATE');
  const initialQuery = {
    page: 1,
    limit: 10,
    sortOrder: 'DESCENDING',
    sortType: 'LATEST',
    ticketName: '',
    branchId: user?.branch?.branchId,
    headBranchId: user?.headBranch?.headBranchId,
    sportTypes: [],
    classTypes: [],
    saleStatuses: [],
    usageCounts: [],
    usagePeriods: [],
  };

  const [query, setQuery] = useState(() => {
    const saved = sessionStorage.getItem('ticketSearchConditions');
    if (saved) {
      sessionStorage.removeItem('ticketSearchConditions');
      try { return JSON.parse(saved); } catch { return initialQuery; }
    }
    return initialQuery;
  });
  const [filterOpen, setFilterOpen] = useState(false);
  const [users, setUsers] = useState();
  const [sportType, setSportType] = useState([]);
  const [customCount, setCustomCount] = useState('');
  const [customPeriod, setCustomPeriod] = useState('');
  const [exposureAlertOpen, setExposureAlertOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);

  const { handleSubmit, control, setValue, reset, watch } = useForm({
    defaultValues: initialQuery,
  });

  const watchSportTypes = watch('sportTypes');
  const watchClassTypes = watch('classTypes');
  const watchSaleStatuses = watch('saleStatuses');
  const watchUsageCounts = watch('usageCounts');
  const watchUsagePeriods = watch('usagePeriods');

  const handleChangePage = (_, value) => {
    setQuery({ ...query, page: value });
  };

  const handleChange = (e, key) => {
    const value = e.target.value;
    setValue(key, value);
    setQuery({ ...query, [key]: value });
  };

  const ticketList = async () => {
    if (!user.headBranch?.headBranchId || (user.centerType === 'BRANCH' && !user.branch?.branchId)) {
      toast.warning(t('Please select a point'));
      setUsers([]);
      setQuery(initialQuery);
    } else {
      const queryString = qs.stringify(query);
      try {
        await getAllTickets(queryString, user.token).then(res => {
          setUsers(res);
        });
      } catch (error) {
        toast.error(errorMessages(error));
      }
    }
  };

  // 지점별 sportTypes 조회 (centerId로 조회)
  const centerInfo = async () => {
    try {
      const response = await centersDetail(user?.token, user?.branch?.centerId);
      // 배열을 { label, value } 형식으로 변환
      const formattedSportTypes = (response?.branchDetailInfo?.sportTypes || []).map(type => ({
        label: t(type),
        value: type,
      }));
      setSportType(formattedSportTypes);
    } catch (error) {
      console.log('centerInfo 함수 에러 : ' + error);
    }
  };

  const submit = async values => {
    setQuery({ ...query, ...values, branchId: user?.branch?.branchId, headBranchId: user?.headBranch?.headBranchId });
  };

  const handleClick = e => {
    if (!user.branch) {
      e.preventDefault();
      toast.warning(t('Please select a point'));
    }
  };

  useEffect(() => {
    setQuery(prevQuery => ({
      ...prevQuery,
      branchId: user?.branch?.branchId,
      headBranchId: user?.headBranch?.headBranchId,
    }));
  }, [user]);

  useEffect(() => {
    ticketList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    if (user?.branch?.centerId) {
      centerInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.branch?.centerId]);

  useEffect(() => {
    localStorage.removeItem('ticket');
  }, []);

  const setInitial = () => {
    setQuery(initialQuery);
    reset(initialQuery);
    setCustomCount('');
    setCustomPeriod('');
  };

  // 전체 체크박스 핸들러
  const handleAllCheck = fieldName => {
    setValue(fieldName, []);
    setQuery(prev => ({ ...prev, [fieldName]: [] }));
  };

  // 판매옵션(횟수/기간) 필터는 폼 상태에만 반영하고, 검색 버튼(submit) 시 query에 적용한다.
  // 다른 필터(종목/클래스/판매상태)와 동일하게 동작시켜 체크 즉시 검색되는 것을 막는다.
  const handleCountChange = value => {
    const currentCounts = watch('usageCounts') || [];
    const newCounts = currentCounts.includes(value)
      ? currentCounts.filter(v => v !== value)
      : [...currentCounts, value];
    setValue('usageCounts', newCounts);
  };

  const handlePeriodChange = value => {
    const currentPeriods = watch('usagePeriods') || [];
    const newPeriods = currentPeriods.includes(value)
      ? currentPeriods.filter(v => v !== value)
      : [...currentPeriods, value];
    setValue('usagePeriods', newPeriods);
  };

  const handleCustomCountChange = e => {
    const value = e.target.value;
    setCustomCount(value);
    if (value) {
      const numValue = parseInt(value, 10);
      const currentCounts = watch('usageCounts') || [];
      if (!isNaN(numValue) && !currentCounts.includes(numValue)) {
        setValue('usageCounts', [...currentCounts, numValue]);
      }
    }
  };

  const handleCustomPeriodChange = e => {
    const value = e.target.value;
    setCustomPeriod(value);
    if (value) {
      const numValue = parseInt(value, 10);
      const currentPeriods = watch('usagePeriods') || [];
      if (!isNaN(numValue) && !currentPeriods.includes(numValue)) {
        setValue('usagePeriods', [...currentPeriods, numValue]);
      }
    }
  };

  return (
    <>
    <AnimationPage>
      <div>
        <PageContainer>
          {/* 검색 섹션 */}
          <SearchSection>
            <SearchInputWrapper>
              <SearchLabel className="title">{t('Search Ticket')}</SearchLabel>
              <FormInputText
                control={control}
                sx={{ width: '370px', mr: '12px' }}
                name="ticketName"
                placeholder={t('Type the ticket title')}
                onKeyDown={event => {
                  if (event.key === 'Enter') {
                    handleSubmit(submit)();
                  }
                }}
              />
              <CommonSearchSubmitButton onClick={handleSubmit(submit)}>{t('Search')}</CommonSearchSubmitButton>
            </SearchInputWrapper>
            <Tooltip title={!canCreate ? '권한이 없습니다' : ''} arrow>
              <Box>
                <CreateButton
                  {...(canCreate ? { component: Link, to: '/ticket/create', onClick: handleClick } : {})}
                  disabled={!canCreate}
                  variant="outlined">
                  <PlusIcon />
                  {t('Create Ticket')}
                </CreateButton>
              </Box>
            </Tooltip>
          </SearchSection>

          {/* 필터 섹션 */}
          <Collapse in={filterOpen}>
          <FilterRow>
            <FilterLabel>{t('종목')}</FilterLabel>
            <FilterContent>
              <AllCheckboxWrapper>
                <AllCheckboxLabel
                  control={
                    <StyledCheckbox
                      checked={!watchSportTypes || watchSportTypes.length === 0}
                      onChange={() => handleAllCheck('sportTypes')}
                      icon={<img src="/images/icons/unchecked.svg" alt="unchecked" />}
                      checkedIcon={<img src="/images/icons/checked.svg" alt="checked" />}
                    />
                  }
                  label={t('전체')}
                />
              </AllCheckboxWrapper>
              <FilterDivider />
              <RHFCheckbox
                control={control}
                row
                name="sportTypes"
                spacing={0}
                options={sportType}
                sx={{ '& .MuiFormControlLabel-root': { width: '100px', mr: '24px' } }}
              />
            </FilterContent>
          </FilterRow>
          <FilterRow>
            <FilterLabel>{t('클래스 유형')}</FilterLabel>
            <FilterContent>
              <AllCheckboxWrapper>
                <AllCheckboxLabel
                  control={
                    <StyledCheckbox
                      checked={!watchClassTypes || watchClassTypes.length === 0}
                      onChange={() => handleAllCheck('classTypes')}
                      icon={<img src="/images/icons/unchecked.svg" alt="unchecked" />}
                      checkedIcon={<img src="/images/icons/checked.svg" alt="checked" />}
                    />
                  }
                  label={t('전체')}
                />
              </AllCheckboxWrapper>
              <FilterDivider />
              <RHFCheckbox
                control={control}
                row
                name="classTypes"
                spacing={0}
                options={TICKET_CLASS_TYPES}
                sx={{ '& .MuiFormControlLabel-root': { width: '100px', mr: '24px' } }}
              />
            </FilterContent>
          </FilterRow>
          <FilterRow>
            <FilterLabel>{t('판매 상태')}</FilterLabel>
            <FilterContent>
              <AllCheckboxWrapper>
                <AllCheckboxLabel
                  control={
                    <StyledCheckbox
                      checked={!watchSaleStatuses || watchSaleStatuses.length === 0}
                      onChange={() => handleAllCheck('saleStatuses')}
                      icon={<img src="/images/icons/unchecked.svg" alt="unchecked" />}
                      checkedIcon={<img src="/images/icons/checked.svg" alt="checked" />}
                    />
                  }
                  label={t('전체')}
                />
              </AllCheckboxWrapper>
              <FilterDivider />
              <RHFCheckbox
                control={control}
                row
                name="saleStatuses"
                spacing={0}
                options={salesStatus}
                sx={{ '& .MuiFormControlLabel-root': { width: '100px', mr: '24px' } }}
              />
            </FilterContent>
          </FilterRow>

          {/* 판매 옵션 */}
          <SalesOptionSection>
            <SalesOptionLabel>{t('판매 옵션')}</SalesOptionLabel>
            <SalesOptionContent>
              <SalesOptionRow>
                <SubLabel>{t('횟수')}</SubLabel>
                <FilterDivider />
                {COUNT_OPTIONS.map(option => (
                  <CheckboxLabel
                    key={option.value}
                    control={
                      <StyledCheckbox
                        checked={watchUsageCounts?.includes(option.value)}
                        onChange={() => handleCountChange(option.value)}
                        icon={<img src="/images/icons/unchecked.svg" alt="unchecked" />}
                        checkedIcon={<img src="/images/icons/checked.svg" alt="checked" />}
                      />
                    }
                    label={option.label}
                  />
                ))}
                <DirectInputWrapper>
                  <DirectInputLabel>{t('직접 입력')}</DirectInputLabel>
                  <DirectInputField
                    type="number"
                    value={customCount}
                    onChange={handleCustomCountChange}
                    placeholder="0"
                  />
                  <DirectInputUnit>{t('회')}</DirectInputUnit>
                </DirectInputWrapper>
              </SalesOptionRow>
              <SalesOptionRow>
                <SubLabel>{t('기간')}</SubLabel>
                <FilterDivider />
                {PERIOD_OPTIONS.map(option => (
                  <CheckboxLabel
                    key={option.value}
                    control={
                      <StyledCheckbox
                        checked={watchUsagePeriods?.includes(option.value)}
                        onChange={() => handlePeriodChange(option.value)}
                        icon={<img src="/images/icons/unchecked.svg" alt="unchecked" />}
                        checkedIcon={<img src="/images/icons/checked.svg" alt="checked" />}
                      />
                    }
                    label={option.label}
                  />
                ))}
                <DirectInputWrapper>
                  <DirectInputLabel>{t('직접 입력')}</DirectInputLabel>
                  <DirectInputField
                    type="number"
                    value={customPeriod}
                    onChange={handleCustomPeriodChange}
                    placeholder="0"
                  />
                  <DirectInputUnit>{t('개월')}</DirectInputUnit>
                </DirectInputWrapper>
              </SalesOptionRow>
            </SalesOptionContent>
          </SalesOptionSection>

          {/* 필터 버튼 */}
          <FilterButtonWrapper>
            <CommonFilterActionButton variant="primary" onClick={handleSubmit(submit)}>
              {t('Apply and Search')}
            </CommonFilterActionButton>
            <CommonFilterActionButton onClick={setInitial} variant="secondary" color="inherit">
              {t('Clear Filters')}
            </CommonFilterActionButton>
          </FilterButtonWrapper>
          </Collapse>

          <CommonFilterToggle open={filterOpen} onClick={() => setFilterOpen(prev => !prev)} />

          {/* 테이블 컨트롤 */}
          <TableControlsWrapper>
            <PageInfo>총 {users?.totalElements || 0}건</PageInfo>
            <SortWrapper>
              <GuideButton onClick={() => setGuideOpen(true)} sx={{ mr: 1 }}>📖 수강권 가이드</GuideButton>
              <FormInputDropdown
                name="sortOrder"
                onChange={e => handleChange(e, 'sortOrder')}
                control={control}
                options={order.map(type => ({
                  ...type,
                  label: t(type.label),
                }))}
                sx={{ width: '100px', height: 40 }}
              />
              <FormInputDropdown
                name="sortType"
                onChange={e => handleChange(e, 'sortType')}
                control={control}
                options={sort1.map(type => ({
                  ...type,
                  label: t(type.label),
                }))}
                sx={{ minWidth: 110, height: 40, mx: 1 }}
              />
              <FormInputDropdown
                control={control}
                name="limit"
                options={pageSize}
                staticValue={query.limit}
                onChange={e => {
                  const newLimit = e.target.value;
                  setValue('limit', newLimit);
                  setQuery({ ...query, limit: newLimit, page: 1 });
                }}
                sx={{ width: 130 }}
              />
            </SortWrapper>
          </TableControlsWrapper>

          {/* 테이블 */}
          <TableContainer style={{ marginTop: '10px', border: '1px solid #ddd', borderRadius: '5px' }}>
            <Table size="small" aria-label="a dense table">
              <TableHead style={{ background: '#F0F2F5' }}>
                <TableRow style={{ height: '40px' }}>
                  <StyledTableCell align="center" sx={{ width: '40px', padding: 0 }} />
                  <StyledTableCell align="center">{t('Studio')}</StyledTableCell>
                  <StyledTableCell align="center">{t('Class Type')}</StyledTableCell>
                  <StyledTableCell align="center">{t('Pass Title')}</StyledTableCell>
                  <StyledTableCell align="center">{t('Sports')}</StyledTableCell>
                  <StyledTableCell align="center">{t('Period')}</StyledTableCell>
                  <StyledTableCell align="center">{t('Number of Lessons')}</StyledTableCell>
                  <StyledTableCell align="center">{t('Selling Price')}</StyledTableCell>
                  {/* <StyledTableCell align="center">{t('Save Count')}</StyledTableCell> */}
                  <StyledTableCell align="center">앱 노출</StyledTableCell>
                  <StyledTableCellLast align="center">{t('Sales Status')}</StyledTableCellLast>
                </TableRow>
              </TableHead>
              <TableBody>
                {users?.content?.length === 0 && (
                  <TableRow>
                    <StyledBodyCell colSpan={10} align="center" sx={{ padding: '60px 0', color: '#999', fontSize: '14px', borderRight: 'none' }}>
                      검색 결과가 없습니다.
                    </StyledBodyCell>
                  </TableRow>
                )}
                {users?.content?.map((ticket, index) => (
                  <StyledTableRow component={Link} key={index} onClick={e => { handleClick(e); sessionStorage.setItem('ticketSearchConditions', JSON.stringify(query)); }} to={`/ticket/${ticket.id}`} ended={ticket.saleStatus === 'EXPIRED' || ticket.saleStatus === 'SALE_END'}>
                    {/* 즐겨찾기 — 토글 후 목록을 다시 불러 즐겨찾기 우선 정렬을 맞춘다 */}
                    <StyledBodyCell align="center" sx={{ padding: 0 }}>
                      <FavoriteStar
                        targetType={FAVORITE_TARGET.TICKET}
                        targetId={ticket.id}
                        favorite={ticket.favorite}
                        onChanged={ticketList}
                      />
                    </StyledBodyCell>
                    <StyledBodyCell align="center" component="th" scope="row">
                      {ticket.branchShare?.shared ? (
                        <BranchShareText>
                          <span>판매 {saleBranchLabel(ticket.branchShare)}</span>
                          <span>이용 {usageBranchLabel(ticket.branchShare)}</span>
                        </BranchShareText>
                      ) : (
                        ticket.storeName ?? ticket.branchName
                      )}
                    </StyledBodyCell>
                    <StyledBodyCell align="center" component="th" scope="row">
                      {t(ticket.classType)}
                    </StyledBodyCell>
                    <StyledBodyCell align="center">{ticket.ticketName}</StyledBodyCell>
                    <StyledBodyCell align="center">
                      {ticket.sportTypes?.length
                        ? ticket.sportTypes.map(s => t(s)).join(', ')
                        : t(ticket.sportType)}
                    </StyledBodyCell>
                    <StyledBodyCell align="center">
                      {ticket.usagePeriod} {ticket.usagePeriodType === 'MONTH' ? t('Month(s)') : t('Day(s)')}
                    </StyledBodyCell>
                    <StyledBodyCell align="center">{ticket.usageCount}</StyledBodyCell>
                    <StyledBodyCell align="center">{formatterCurrency(ticket.finalPrice)}</StyledBodyCell>
                    {/* <StyledBodyCell align="center">
                      {ticket.wishListAddedCount ? ticket.wishListAddedCount : '-'}
                    </StyledBodyCell> */}
                    <StyledBodyCell align="center">
                      <div onClick={e => e.stopPropagation()}>
                      <Switch
                        size="small"
                        checked={ticket.exposureYn === 'Y'}
                        onChange={async e => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (ticket.saleStatus === 'EXPIRED' || ticket.saleStatus === 'SALE_END') {
                            setExposureAlertOpen(true);
                            return;
                          }
                          const newValue = ticket.exposureYn === 'Y' ? 'N' : 'Y';
                          try {
                            await toggleTicketExposure(user.token, ticket.id, newValue);
                            setUsers(prev => ({
                              ...prev,
                              content: prev.content.map(t => t.id === ticket.id ? { ...t, exposureYn: newValue } : t),
                            }));
                            toast.success(newValue === 'Y' ? '앱에 노출됩니다' : '앱에서 숨겨집니다');
                          } catch { toast.error('변경에 실패했습니다'); }
                        }}
                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#4AB3BC' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4AB3BC' } }}
                      />
                      </div>
                    </StyledBodyCell>
                    <StyledBodyCellLast align="center">
                      <StatusText status={ticket.saleStatus}>{t(`TICKET_${ticket.saleStatus}`)}</StatusText>
                    </StyledBodyCellLast>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* 페이지네이션 */}
          <CommonPagination
            totalElements={users?.totalElements}
            limit={query.limit}
            page={query.page}
            onChange={handleChangePage}
          />
        </PageContainer>
      </div>
    </AnimationPage>

    <ConfirmModal
      open={exposureAlertOpen}
      onClose={() => setExposureAlertOpen(false)}
      onConfirm={() => setExposureAlertOpen(false)}
      message={`판매종료된 상품은\n앱 노출이 불가합니다.`}
      highlightText="__none__"
      highlightColor="#4AB3BC"
      cancelText="닫기"
      confirmText="확인"
      confirmColor="#4AB3BC"
    />

    <TicketGuideModal open={guideOpen} onClose={() => setGuideOpen(false)} />
    </>
  );
};

export default TicketList;
